import { Person } from '@mui/icons-material';
import { Divider, useMediaQuery } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useWorktimeReportData,
} from '~/data/reports';
import { SavedReportSettingsType } from '~/data/reportSettings';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  SavedReportSettingsSelector,
  useReportDataset,
  useSavedReportSettingsConfig,
} from '~/scenes/PortalModules/Analytics/common';

import { useWorktimeReport } from './useWorktimeReport';

export const WorktimeReport = () => {
  const { t } = useTranslation();

  const isSmallScreen = useMediaQuery('(max-height:650px)');

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const apiRef = useGridApiRef();

  const defaultReportSelection: AnalyticsReportProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      startDate: moment().startOf('month').toDate(),
      endDate: moment().toDate(),
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
      },
      fallbackToEmployeeReportType: true,
    },
  });

  const reportSettingsConfig = useSavedReportSettingsConfig({
    reportType: SavedReportSettingsType.WorktimeReport,
    reportDataset: reportDatasetProps,
    apiRef,
  });

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useWorktimeReportData<object>(reportDatasetProps.reportSelection);

  const { table } = useWorktimeReport({ data });

  return (
    <ReportViewLayout
      heading={t('laWorktimeReport')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout showHeading={true}>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
                renderCurrentParams={({ params }) => {
                  return (
                    <>{`${moment(params?.startDate).format('L')} - ${moment(
                      params?.endDate,
                    ).format('L')}`}</>
                  );
                }}
                renderParamsFields={({ params, onChange }) => {
                  return (
                    <ReportDateRangeSelector
                      startDate={params.startDate}
                      endDate={params.endDate}
                      onRangeChange={(startDate, endDate) =>
                        onChange({
                          ...params,
                          startDate: startDate ?? params.startDate,
                          endDate: endDate ?? params.endDate,
                        })
                      }
                      mode={ReportDateRangeSelectorMode.DateRange}
                    />
                  );
                }}
              />
              <ReportDatasetRowCount
                count={table.rows.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <SavedReportSettingsSelector
                {...reportSettingsConfig}
                apiRef={apiRef}
                reportDataset={reportDatasetProps}
                isLoading={isLoadingReportData}
              />
              {reportSettingsConfig.isSavedReportSettingsEnabled && (
                <Divider orientation={'vertical'} />
              )}
              <ReportColumnsVisibility
                apiRef={apiRef}
                columnGroupingModel={table.columnGroupingModel}
                isLoading={isLoadingReportData}
              />
              <ReportDataExport
                title={t('laWorktimeReport')}
                startDate={reportDatasetProps.reportSelection.params?.startDate}
                apiRef={apiRef}
                isLoading={isLoadingReportData}
              />
            </>
          ),
        },
      }}
      slotProps={{
        content: {
          sx: {
            minHeight: isSmallScreen ? 'calc(100vh - 100px)' : 0,
          },
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={table.columnConfigs}
        rows={table.rows}
        columnGroupingModel={table.columnGroupingModel}
        getRowId={(row) => row.uniqueId}
        pinnedColumns={{
          left: ['empId', 'empFirstName', 'empLastName'],
        }}
        columnHeaderHeight={36}
      />
    </ReportViewLayout>
  );
};
